import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import AdminNav from '../components/AdminNav'

const BLUE   = '#1877F2'
const TEXT   = '#050505'
const MUTED  = '#65676B'
const BORDER = '#E4E6EB'
const BG     = '#F0F2F5'
const WHITE  = '#FFFFFF'
const GREEN  = '#16A34A'
const AMBER  = '#D97706'
const RED    = '#DC2626'

const STATUS_STYLE = {
  new:       { bg: '#FEF2F2', color: RED,   label: 'New'       },
  contacted: { bg: '#FFFBEB', color: AMBER, label: 'Contacted' },
  resolved:  { bg: '#F0FDF4', color: GREEN, label: 'Resolved'  },
}

function Icon({ path, size = 15, color = MUTED, sw = 1.8 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
      <path d={path} />
    </svg>
  )
}

const IC = {
  search: 'M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z',
  mail:   'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  phone:  'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
  wa:     'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347',
}

function AdminHelp() {
  const [requests, setRequests] = useState([])
  const [loading, setLoading]   = useState(true)
  const [filter, setFilter]     = useState('new')
  const [selected, setSelected] = useState(null)
  const [updating, setUpdating] = useState(false)
  const [note, setNote]         = useState('')
  const [search, setSearch]     = useState('')
  const [noteFocused, setNoteFocused] = useState(false)

  useEffect(() => { fetchRequests() }, [])

  const fetchRequests = async () => {
    setLoading(true)
    const { data } = await supabase
      .from('help_requests')
      .select('*, sellers(shop_name, email, phone, facebook_url, district, plan, subscription_plan)')
      .order('created_at', { ascending: false })
    setRequests(data || [])
    setLoading(false)
  }

  const updateStatus = async (id, status) => {
    setUpdating(true)
    await supabase.from('help_requests').update({ status, admin_note: note || null }).eq('id', id)
    await fetchRequests()
    setSelected(prev => prev ? { ...prev, status } : null)
    setUpdating(false)
  }

  const counts = {
    all:       requests.length,
    new:       requests.filter(r => r.status === 'new').length,
    contacted: requests.filter(r => r.status === 'contacted').length,
    resolved:  requests.filter(r => r.status === 'resolved').length,
  }

  const filtered = requests.filter(r => {
    const matchFilter = filter === 'all' || r.status === filter
    const q = search.toLowerCase()
    const matchSearch = !search || r.sellers?.shop_name?.toLowerCase().includes(q) || r.sellers?.email?.toLowerCase().includes(q) || r.sellers?.phone?.includes(search)
    return matchFilter && matchSearch
  })

  const timeAgo = (d) => {
    const s = Math.floor((new Date() - new Date(d)) / 1000)
    if (s < 60) return 'just now'
    if (s < 3600) return Math.floor(s / 60) + 'm ago'
    if (s < 86400) return Math.floor(s / 3600) + 'h ago'
    return Math.floor(s / 86400) + 'd ago'
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: BG, fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
      <AdminNav />
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <div style={{ padding: '24px 28px' }}>
          <style>{`
            @media(max-width:900px){ .help-layout{flex-direction:column!important} .help-list{width:100%!important; max-height:400px} }
          `}</style>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 800, color: TEXT, letterSpacing: '-0.3px' }}>Help Requests</div>
              <div style={{ fontSize: '12.5px', color: MUTED, marginTop: '2px' }}>
                {counts.new > 0 && <span style={{ color: RED, fontWeight: 700 }}>{counts.new} new · </span>}
                {counts.contacted} contacted · {counts.resolved} resolved
              </div>
            </div>
            <button onClick={fetchRequests} style={{ fontSize: '12px', fontWeight: 600, color: MUTED, background: WHITE, border: '1px solid ' + BORDER, padding: '7px 14px', borderRadius: '9px', cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>
              ↻ Refresh
            </button>
          </div>

          {loading && (
            <div style={{ textAlign: 'center', padding: '60px', color: MUTED, fontSize: '13px' }}>Loading help requests...</div>
          )}

          {!loading && (
            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }} className="help-layout">

              {/* LEFT: List */}
              <div style={{ width: '340px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '10px' }} className="help-list">

                {/* Filter tabs */}
                <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '12px' }}>
                  <div style={{ display: 'flex', gap: '4px', marginBottom: '10px' }}>
                    {['all','new','contacted','resolved'].map(s => (
                      <button key={s} onClick={() => setFilter(s)}
                        style={{ flex: 1, padding: '6px 4px', borderRadius: '8px', border: 'none', fontSize: '11px', fontWeight: 700, cursor: 'pointer', background: filter === s ? (s === 'new' ? RED : s === 'contacted' ? AMBER : s === 'resolved' ? GREEN : BLUE) : BG, color: filter === s ? WHITE : MUTED, fontFamily: 'Space Grotesk, sans-serif', textTransform: 'capitalize', transition: 'all 0.12s' }}>
                        {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                        <span style={{ display: 'block', fontSize: '13px', fontWeight: 800, lineHeight: 1.2 }}>{counts[s]}</span>
                      </button>
                    ))}
                  </div>
                  <div style={{ position: 'relative' }}>
                    <input type="text" placeholder="Search shop or email..." value={search}
                      onChange={e => setSearch(e.target.value)}
                      style={{ width: '100%', border: '1px solid ' + BORDER, borderRadius: '9px', padding: '7px 12px 7px 30px', fontSize: '12px', outline: 'none', background: BG, color: TEXT, fontFamily: 'Space Grotesk, sans-serif', boxSizing: 'border-box' }}
                      onFocus={e => e.target.style.borderColor = BLUE}
                      onBlur={e => e.target.style.borderColor = BORDER} />
                    <div style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                      <Icon path={IC.search} color={MUTED} size={13} />
                    </div>
                  </div>
                </div>

                {/* Request list */}
                <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, overflow: 'hidden' }}>
                  {filtered.length === 0 ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: MUTED, fontSize: '12px' }}>No requests found</div>
                  ) : filtered.map((req, i) => {
                    const ss = STATUS_STYLE[req.status] || STATUS_STYLE.new
                    const isSelected = selected?.id === req.id
                    return (
                      <div key={req.id}
                        onClick={() => setSelected(req)}
                        style={{ padding: '12px 14px', borderBottom: i < filtered.length - 1 ? '1px solid #F5F5F5' : 'none', cursor: 'pointer', background: isSelected ? '#EFF6FF' : WHITE, borderLeft: isSelected ? '3px solid ' + BLUE : '3px solid transparent', transition: 'all 0.1s' }}
                        onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = BG }}
                        onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = WHITE }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '3px' }}>
                          <span style={{ fontSize: '13px', fontWeight: 700, color: TEXT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, marginRight: '8px' }}>{req.sellers?.shop_name || req.shop_name || '—'}</span>
                          <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '20px', background: ss.bg, color: ss.color, flexShrink: 0 }}>{ss.label}</span>
                        </div>
                        <div style={{ fontSize: '11.5px', color: MUTED, marginBottom: '2px', textTransform: 'capitalize' }}>{(req.request_type || 'General').replace(/_/g, ' ')}</div>
                        <div style={{ fontSize: '11px', color: MUTED }}>{timeAgo(req.created_at)}</div>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* RIGHT: Detail */}
              {selected && (
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>

                  {/* Header card */}
                  <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '18px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '18px', fontWeight: 800, color: TEXT, marginBottom: '2px' }}>{selected.sellers?.shop_name || selected.shop_name}</div>
                        <div style={{ fontSize: '12.5px', color: MUTED, textTransform: 'capitalize', marginBottom: '8px' }}>{(selected.request_type || 'General').replace(/_/g, ' ')}</div>
                        <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '20px', background: STATUS_STYLE[selected.status]?.bg, color: STATUS_STYLE[selected.status]?.color }}>
                          {STATUS_STYLE[selected.status]?.label}
                        </span>
                      </div>
                      <button onClick={() => setSelected(null)} style={{ fontSize: '12px', fontWeight: 600, color: MUTED, background: BG, border: '1px solid ' + BORDER, padding: '7px 12px', borderRadius: '8px', cursor: 'pointer', flexShrink: 0, fontFamily: 'Space Grotesk, sans-serif' }}>
                        ✕ Close
                      </button>
                    </div>
                  </div>

                  {/* Info + Message */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '16px 18px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '12px' }}>Seller info</div>
                      {[
                        { label: 'Email', value: selected.sellers?.email || selected.email },
                        { label: 'Phone', value: selected.sellers?.phone || selected.phone },
                        { label: 'District', value: selected.sellers?.district },
                        { label: 'Plan', value: selected.sellers?.subscription_plan || selected.sellers?.plan || 'trial' },
                        { label: 'Submitted', value: new Date(selected.created_at).toLocaleDateString('en-BD', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) },
                      ].map(({ label, value }) => value && (
                        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', gap: '8px' }}>
                          <span style={{ fontSize: '12px', color: MUTED, flexShrink: 0 }}>{label}</span>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: TEXT, textAlign: 'right', wordBreak: 'break-word' }}>{value}</span>
                        </div>
                      ))}
                    </div>

                    <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '16px 18px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '12px' }}>Message</div>
                      {selected.message ? (
                        <div style={{ fontSize: '13px', color: TEXT, lineHeight: 1.6, background: BG, borderRadius: '8px', padding: '12px' }}>{selected.message}</div>
                      ) : (
                        <div style={{ fontSize: '12px', color: MUTED, fontStyle: 'italic' }}>No message provided</div>
                      )}
                      {selected.admin_note && (
                        <div style={{ marginTop: '10px' }}>
                          <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, marginBottom: '4px' }}>Admin note</div>
                          <div style={{ fontSize: '12px', color: TEXT, background: '#FFFBEB', borderRadius: '8px', padding: '10px', border: '1px solid #FDE68A' }}>{selected.admin_note}</div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Quick contact */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <a href={'https://wa.me/880' + (selected.sellers?.phone || selected.phone || '').replace(/^0/, '')}
                      target="_blank" rel="noreferrer"
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '13px', borderRadius: '10px', background: '#F0FDF4', border: '1px solid #BBF7D0', textDecoration: 'none', fontSize: '13px', fontWeight: 700, color: GREEN }}>
                      <Icon path={IC.wa} color={GREEN} size={16} sw={2} />
                      WhatsApp
                    </a>
                    <a href={'mailto:' + (selected.sellers?.email || selected.email)}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '13px', borderRadius: '10px', background: '#EFF6FF', border: '1px solid #BFDBFE', textDecoration: 'none', fontSize: '13px', fontWeight: 700, color: BLUE }}>
                      <Icon path={IC.mail} color={BLUE} size={16} sw={2} />
                      Send email
                    </a>
                  </div>

                  {/* Status actions */}
                  <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '16px 18px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '12px' }}>Update status</div>
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ fontSize: '11.5px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px' }}>Admin note <span style={{ fontWeight: 400, color: '#B0B3B8' }}>optional</span></label>
                      <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
                        placeholder="e.g. Called seller — issue resolved via WhatsApp"
                        style={{ width: '100%', border: '1px solid ' + (noteFocused ? BLUE : BORDER), borderRadius: '9px', padding: '8px 12px', fontSize: '12px', outline: 'none', background: BG, color: TEXT, fontFamily: 'Space Grotesk, sans-serif', resize: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s' }}
                        onFocus={() => setNoteFocused(true)} onBlur={() => setNoteFocused(false)} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                      {[
                        { status: 'new',       label: 'Reopen',    color: RED,   bg: '#FEF2F2', border: '#FECACA' },
                        { status: 'contacted', label: 'Contacted', color: AMBER, bg: '#FFFBEB', border: '#FDE68A' },
                        { status: 'resolved',  label: 'Resolved',  color: GREEN, bg: '#F0FDF4', border: '#BBF7D0' },
                      ].map(btn => (
                        <button key={btn.status}
                          onClick={() => updateStatus(selected.id, btn.status)}
                          disabled={updating || selected.status === btn.status}
                          style={{ padding: '10px', borderRadius: '9px', border: '1px solid ' + btn.border, background: selected.status === btn.status ? btn.bg : WHITE, fontSize: '12.5px', fontWeight: 700, color: btn.color, cursor: selected.status === btn.status ? 'default' : 'pointer', opacity: selected.status === btn.status ? 0.5 : 1, fontFamily: 'Space Grotesk, sans-serif', transition: 'all 0.12s' }}>
                          {btn.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {!selected && (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, minHeight: '300px' }}>
                  <div style={{ textAlign: 'center', color: MUTED }}>
                    <div style={{ fontSize: '32px', marginBottom: '10px' }}>🎧</div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: TEXT, marginBottom: '4px' }}>Select a request</div>
                    <div style={{ fontSize: '12px' }}>Click any help request to view and respond</div>
                  </div>
                </div>
              )}

            </div>
          )}

        </div>
      </div>
    </div>
  )
}

export default AdminHelp